import React from "react";
import { useNavigate } from "react-router-dom";

const DeFiProtocols = () => {
  const navigate = useNavigate();

  const protocols = [
    {
      name: "GreenSwap",
      description: "Decentralized exchange with near-zero fees",
      tvl: "$42.8M",
      apy: "12-45%",
      icon: "🔄"
    },
    {
      name: "EcoLend",
      description: "Sustainable lending and borrowing platform",
      tvl: "$28.3M",
      apy: "8-22%",
      icon: "💰"
    },
    {
      name: "YieldFarm Pro",
      description: "Optimized yield farming with auto-compounding",
      tvl: "$65.2M",
      apy: "15-68%",
      icon: "🌾"
    },
    {
      name: "StableGreen",
      description: "Algorithmic stablecoin ecosystem",
      tvl: "$89.1M",
      apy: "5-18%",
      icon: "🛡️"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-eco-black to-eco-dark pt-20">
      <div className="max-w-7xl mx-auto px-8 py-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-eco-green hover:text-eco-blue mb-8 transition-colors"
        >
          ← Back to Ecosystem
        </button>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-20 h-20 bg-gradient-to-br from-eco-green to-eco-blue rounded-3xl flex items-center justify-center text-eco-black text-3xl mx-auto mb-6">
            🔄
          </div>
          <h1 className="text-5xl font-black gradient-text mb-4">DeFi Protocols</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Access decentralized finance with lightning-fast transactions and minimal carbon footprint. 
            Our sustainable DeFi ecosystem offers competitive yields with environmental responsibility.
          </p>
        </div>

        {/* Protocols Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {protocols.map((protocol, index) => (
            <div key={index} className="glass-card p-6 rounded-3xl border border-eco-green border-opacity-20 hover:border-eco-green hover:border-opacity-40 transition-all duration-300 group cursor-pointer">
              <div className="flex items-start mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-eco-green to-eco-blue rounded-2xl flex items-center justify-center text-eco-black text-xl mr-4">
                  {protocol.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">{protocol.name}</h3>
                  <p className="text-gray-400 text-sm">{protocol.description}</p>
                </div>
              </div>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="text-sm text-gray-400">TVL</div>
                  <div className="text-lg font-bold text-eco-green">{protocol.tvl}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400">APY Range</div>
                  <div className="text-lg font-bold text-eco-blue">{protocol.apy}</div>
                </div>
              </div>
              <button className="w-full py-3 bg-eco-dark border border-eco-green border-opacity-30 rounded-xl text-eco-green font-semibold hover:bg-eco-green hover:bg-opacity-10 transition-all duration-300">
                Explore Protocol
              </button>
            </div>
          ))}
        </div>

        {/* Benefits Section */}
        <div className="glass-card rounded-3xl p-8 mb-16 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Why Choose Our DeFi?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-eco-green rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Lightning Fast</h3>
              <p className="text-gray-300">Sub-second transaction finality with instant settlement</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-eco-blue rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
                🌱
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Eco-Friendly</h3>
              <p className="text-gray-300">99.9% less energy consumption than traditional DeFi</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-eco-purple rounded-2xl flex items-center justify-center text-eco-black text-2xl mx-auto mb-4">
                💸
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Low Fees</h3>
              <p className="text-gray-300">Near-zero transaction fees with optimized gas usage</p>
            </div>
          </div>
        </div>

        {/* Security Features */}
        <div className="glass-card rounded-3xl p-8 border border-eco-green border-opacity-20">
          <h2 className="text-3xl font-black text-white text-center mb-8">Security & Audits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Multi-layer Security</h3>
              <ul className="space-y-3">
                <li className="flex items-center text-eco-green">
                  <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                  Smart contract audits by leading firms
                </li>
                <li className="flex items-center text-eco-green">
                  <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                  Bug bounty programs with $1M+ rewards
                </li>
                <li className="flex items-center text-eco-green">
                  <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                  Insurance coverage for all protocols
                </li>
                <li className="flex items-center text-eco-green">
                  <span className="w-2 h-2 bg-eco-green rounded-full mr-3"></span>
                  Real-time monitoring and alerts
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-4">Audit Partners</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-eco-dark rounded-2xl p-4 text-center border border-gray-700">
                  <div className="text-2xl mb-2">🔒</div>
                  <div className="text-sm font-bold text-white">CertiK</div>
                </div>
                <div className="bg-eco-dark rounded-2xl p-4 text-center border border-gray-700">
                  <div className="text-2xl mb-2">🛡️</div>
                  <div className="text-sm font-bold text-white">Hacken</div>
                </div>
                <div className="bg-eco-dark rounded-2xl p-4 text-center border border-gray-700">
                  <div className="text-2xl mb-2">✅</div>
                  <div className="text-sm font-bold text-white">Quantstamp</div>
                </div>
                <div className="bg-eco-dark rounded-2xl p-4 text-center border border-gray-700">
                  <div className="text-2xl mb-2">🔍</div>
                  <div className="text-sm font-bold text-white">PeckShield</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeFiProtocols;